import React, { useMemo } from 'react';
import { APP_VERSION_FULL } from '../../version';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Activity,
  Building2,
  FileCheck,
  FileText,
  AlertTriangle,
  Bell,
  Users,
  Lock,
  Search,
  Power,
  Clock,
  PlusSquare,
  BookOpen,
  Sun,
  Moon,
  ChevronRight,
  Monitor,
  Languages,
  KeyRound,
  Database,
  Network,
  LayoutDashboard,
  ShieldAlert,
  ShieldCheck,
  BarChart2,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoImg from '../../assets/logo.png';
import XORAAssistant from '../common/XORAAssistant';

// ── JWT decode (không cần thư viện) ──────────────────────────────────────────
function parseJwt(token: string): any {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

interface MainLayoutProps {
  children: React.ReactNode;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  portalType: 'admin' | 'user' | 'agent';
}

const LANGUAGES = [
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' }
];

export default function MainLayout({ children, theme, onToggleTheme, portalType }: MainLayoutProps) {
  const location = useLocation();
  const navigate  = useNavigate();
  const { t, i18n } = useTranslation();

  // ── Decode current user từ JWT ──────────────────────────────────────────────
  const currentUser = useMemo(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    const payload = parseJwt(token);
    return payload
      ? {
          username:  payload.username  as string,
          fullName:  payload.fullName  as string | undefined,
          roles:     (payload.roles   as string[]) ?? [],
          sub:       payload.sub      as string,
        }
      : null;
  }, [location.pathname]); // re-parse khi navigate (token có thể được refresh)

  const displayName = currentUser?.fullName ?? currentUser?.username ?? '—';
  const initials    = displayName.split(' ').map((n: string) => n[0]).slice(-2).join('').toUpperCase();
  const isSysAdmin  = currentUser?.roles?.includes('ROLE_SYS_ADMIN');

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/login');
  };

  const isAdmin = portalType === 'admin';
  const isAgent = portalType === 'agent';
  const portalTitle = isAdmin
    ? t('layout.portalTitle.admin')
    : isAgent ? 'Agent Node' : t('layout.portalTitle.user');
  const portalSubtitle = isAdmin
    ? t('layout.portalSubtitle.admin')
    : isAgent ? 'XDA On-Premise' : t('layout.portalSubtitle.user');

  const ADMIN_MENU_ITEMS = [
    { icon: Activity, label: t('menu.admin.nationalMonitoring'), path: '/dashboard' },
    { icon: Building2, label: t('menu.admin.orgManagement'), path: '/assessments' },
    { icon: FileCheck, label: t('menu.admin.checklistApproval'), path: '/checklists' },
    { icon: Monitor, label: t('menu.admin.autoAudit'), path: '/auto-audit' },
    { icon: Shield, label: t('menu.admin.securityAudit'), path: '/security-audit' },
    { icon: FileText, label: t('menu.admin.reportConsolidation'), path: '/reports' },
    { icon: AlertTriangle, label: t('menu.admin.remediation'), path: '/remediations' },
    { icon: ShieldCheck, label: 'Forensic Store', path: '/forensic', badge: 'C12' },
  ];

  const PORTAL_MENU_ITEMS = [
    { icon: Clock, label: t('menu.user.reportHistory'), path: '/portal/history' },
    { icon: PlusSquare, label: t('menu.user.newReport'), path: '/portal/report/new' },
    { icon: BookOpen, label: t('menu.user.guidance'), path: '/portal/guidance' },
  ];

  const SYSTEM_MENU_ITEMS = [
    { icon: Bell, label: t('menu.system.alertCenter'), path: '/alerts' },
    { icon: Users, label: t('menu.system.userManagement'), path: '/users' },
    { icon: Lock, label: t('menu.system.ruleConfig'), path: '/settings' },
  ];

  const AGENT_MENU_ITEMS = [
    { icon: LayoutDashboard, label: 'Tổng quan',            path: '/agent/dashboard' },
    { icon: BarChart2,       label: 'KPI Collector',        path: '/agent/kpi-collector', badge: 'AUTO' },
    { icon: KeyRound,        label: 'Quản lý Key',          path: '/agent/keys' },
    { icon: Network,         label: 'Kết nối NDOP/C12',     path: '/agent/connections' },
    { icon: Database,        label: 'Data Sources',         path: '/agent/data-sources' },
    { icon: FileText,        label: 'Tài sản dữ liệu',      path: '/agent/data-assets' },
    { icon: FileCheck,       label: 'Phiếu KPI',   path: '/agent/reports' },
    { icon: ShieldAlert,     label: 'Giám sát bảo mật',     path: '/agent/security' },
  ];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  const renderSection = (title: string, items: { icon: any, label: string, path: string, badge?: string }[]) => (
    <div className="mb-8">
      <p className="px-4 text-[10px] font-medium mb-4 text-slate-500 dark:text-slate-400 tracking-[0.2em]">{title}</p>
      <div className="flex flex-col gap-1.5">
        {items.map((item) => {
          const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.label}
              to={item.path}
              className={`flex items-center gap-3.5 px-4 py-3.5 rounded-2xl text-[12px] font-light transition-all group border tracking-wide ${
                active 
                  ? 'bg-indigo-600 text-white border-indigo-400 shadow-xl shadow-indigo-500/20' 
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 border-transparent hover:border-slate-200 dark:hover:border-white/5'
              }`}
            >
              <item.icon size={18} className={`${active ? 'text-white' : 'text-slate-500 dark:text-slate-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400'}`} />
              <span className="flex-1">{item.label}</span>
              {(item as any).badge && (
                <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider ${active ? 'bg-white/20 text-white' : 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'}`}>
                  {(item as any).badge}
                </span>
              )}
              {active && <ChevronRight size={14} className="opacity-40" />}
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#0B0B0C] transition-colors duration-500 overflow-hidden text-slate-900 dark:text-white font-sans font-light">
      {/* ── TOP NAV ── */}
      <header className="h-[72px] sticky top-0 z-50 border-b border-slate-200 dark:border-white/5 px-8 flex items-center justify-between bg-white/90 dark:bg-black/40 backdrop-blur-2xl transition-all duration-500 shadow-sm relative">
        <div className="flex items-center gap-6">
          <div className="flex items-center group cursor-pointer">
            <img src={logoImg} alt="xDataAudit Logo" className="h-9 group-hover:scale-105 transition-transform" />
          </div>
          <div className="h-8 w-px bg-slate-200 dark:bg-white/10 hidden sm:block" />
          <div className="hidden sm:block">
            <h1 className="text-xl font-heading font-medium tracking-tighter text-slate-900 dark:text-white leading-none branding-title">
              xDataAudit <span className="text-[10px] text-indigo-600 dark:text-indigo-400 ml-1 font-semibold">{portalTitle}</span>
            </h1>
            <p className="text-[9px] text-slate-400 dark:text-slate-600 font-semibold tracking-[0.1em] mt-1">{portalSubtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden lg:flex items-center gap-3 bg-slate-100 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-2xl px-5 py-2.5 focus-within:border-indigo-500/50 focus-within:ring-4 focus-within:ring-indigo-500/10 transition-all shadow-inner">
            <Search size={14} className="text-slate-500 dark:text-slate-400" />
            <input type="text" placeholder={t('layout.searchPlaceholder')} className="bg-transparent text-[11px] outline-none w-72 placeholder-slate-500 dark:placeholder-slate-500 font-light tracking-tight text-slate-900 dark:text-white" />
          </div>

          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative group/lang">
              <button className="w-11 h-11 flex items-center justify-center rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 transition-all shadow-sm active:scale-90">
                <Languages size={20} />
              </button>
              
              <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-[#161617] border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl opacity-0 invisible group-hover/lang:opacity-100 group-hover/lang:visible transition-all z-50 p-2 py-3 overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto no-scrollbar">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[11px] font-medium transition-all ${
                        i18n.language === lang.code 
                          ? 'bg-indigo-600 text-white' 
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button 
              onClick={onToggleTheme}
              className="w-11 h-11 flex items-center justify-center rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 transition-all shadow-sm active:scale-90"
            >
              <AnimatePresence mode="wait">
                 <motion.div
                   key={theme}
                   initial={{ opacity: 0, rotate: -20 }}
                   animate={{ opacity: 1, rotate: 0 }}
                   exit={{ opacity: 0, rotate: 20 }}
                   transition={{ duration: 0.2 }}
                 >
                   {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                 </motion.div>
              </AnimatePresence>
            </button>

            <button className="w-11 h-11 flex items-center justify-center rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:text-indigo-600 transition-all shadow-sm relative group active:scale-90">
              <Bell size={20} className="group-hover:rotate-12 transition-transform" />
              <span className="absolute top-3.5 right-3.5 w-2.5 h-2.5 bg-indigo-500 rounded-full border-[3px] border-white dark:border-[#0B0B0C]" />
            </button>

            <div className="h-8 w-px bg-slate-200 dark:bg-white/10 mx-1" />

            <div className="flex items-center gap-3 group cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 p-1 rounded-2xl transition-all">
              <div className="text-right hidden xl:block">
                <p className="text-[11px] font-black text-slate-900 dark:text-white tracking-tight truncate max-w-[140px]">
                  {displayName}
                </p>
                <p className="text-[9px] text-indigo-600 dark:text-indigo-400 font-medium tracking-widest mt-0.5">
                  {isSysAdmin ? 'System Admin' : currentUser?.roles?.[0]?.replace('ROLE_', '').replace(/_/g, ' ') ?? '—'}
                </p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 border border-white/20 flex items-center justify-center text-[11px] font-black text-white shadow-lg group-hover:scale-105 transition-transform">
                {initials || '?'}
              </div>
            </div>

            <button
              onClick={handleLogout}
              title="Đăng xuất"
              className="w-11 h-11 flex items-center justify-center rounded-2xl text-rose-500/40 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all active:scale-95"
            >
              <Power size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* SIDEBAR */}
        <aside className="w-[300px] shrink-0 border-r border-slate-200 dark:border-white/5 flex flex-col p-8 gap-1 relative z-40 overflow-y-auto no-scrollbar bg-white/50 dark:bg-black/20 backdrop-blur-xl transition-all duration-500 shadow-sm">
          {isAgent ? (
            <>
              {renderSection('AGENT NODE', AGENT_MENU_ITEMS)}
              <div className="mt-auto p-6 rounded-[2.5rem] border border-emerald-100 dark:border-emerald-500/10 bg-emerald-50/30 dark:bg-emerald-500/[0.03] relative overflow-hidden group shadow-inner">
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 blur-[40px] rounded-full" />
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium mb-3 tracking-[0.1em] flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  TRẠNG THÁI NODE
                </p>
                <p className="text-[11px] leading-relaxed font-light text-slate-600 dark:text-slate-400 tracking-tight">
                  XDA On-Premise đang hoạt động. Kết nối NDOP sẵn sàng.
                </p>
                <div className="mt-4 flex items-center gap-2 text-[9px] font-medium text-emerald-500 dark:text-emerald-600 tracking-widest bg-white dark:bg-black/40 w-max px-3 py-1.5 rounded-full border border-emerald-100 dark:border-emerald-900/50">
                  <Monitor size={10} /> {APP_VERSION_FULL} — XDA Agent
                </div>
              </div>
            </>
          ) : !isAdmin ? (
            <>
              {renderSection(t('layout.section.user'), PORTAL_MENU_ITEMS)}
              <div className="mt-auto p-6 rounded-[2.5rem] border border-cyan-100 dark:border-cyan-500/10 bg-cyan-50/30 dark:bg-cyan-500/[0.03] relative overflow-hidden group shadow-inner">
                <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 blur-[40px] rounded-full" />
                <p className="text-[10px] text-cyan-600 dark:text-cyan-400 font-medium mb-3 tracking-[0.1em] flex items-center gap-2">{t('layout.support.title')}</p>
                <p className="text-[11px] leading-relaxed font-light text-slate-600 dark:text-slate-400 tracking-tight">{t('layout.support.desc')}</p>
                <button className="mt-5 w-full py-3 bg-cyan-600 text-white text-[9px] font-medium tracking-widest rounded-2xl shadow-lg shadow-cyan-600/20 hover:shadow-cyan-600/40 transition-all transform hover:-translate-y-0.5 active:scale-95">{t('layout.support.hotline')}</button>
                <div className="mt-4 flex items-center gap-2 text-[9px] font-medium text-cyan-500 dark:text-cyan-600 tracking-widest bg-white dark:bg-black/40 w-max px-3 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-900/50">
                  <Monitor size={10} /> {APP_VERSION_FULL}
                </div>
              </div>
            </>
          ) : (
            <>
              {renderSection(t('layout.section.admin'), ADMIN_MENU_ITEMS)}
              {renderSection(t('layout.section.global'), SYSTEM_MENU_ITEMS)}
              <div className="mt-auto p-6 rounded-[2.5rem] border border-indigo-100 dark:border-indigo-500/10 bg-indigo-50/30 dark:bg-indigo-500/[0.03] relative overflow-hidden group shadow-inner">
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 blur-[40px] rounded-full" />
                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium mb-3 tracking-[0.1em] flex items-center gap-2">{t('layout.soc.title')}</p>
                <p className="text-[11px] leading-relaxed font-light text-slate-600 dark:text-slate-400 tracking-tight">{t('layout.soc.desc')}</p>
                <div className="mt-4 flex items-center gap-2 text-[9px] font-medium text-indigo-500 dark:text-indigo-600 tracking-widest bg-white dark:bg-black/40 w-max px-3 py-1.5 rounded-full border border-indigo-100 dark:border-indigo-900/50">
                   <Monitor size={10} /> {APP_VERSION_FULL}
                </div>
              </div>
            </>
          )}
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 overflow-y-auto relative custom-scrollbar bg-slate-50 dark:bg-[#0B0B0C] transition-colors duration-500 overflow-x-hidden">
           {/* Gradient Shadows in Light Mode / Glows in Dark Mode */}
           <div className="fixed inset-0 pointer-events-none -z-10 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-indigo-500/5 dark:from-indigo-500/[0.02] to-transparent" />
              {theme === 'dark' && (
                <>
                  <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-indigo-500/[0.03] blur-[180px] rounded-full" />
                  <div className="absolute bottom-[10%] left-[10%] w-[40%] h-[40%] bg-cyan-500/[0.02] blur-[150px] rounded-full" />
                </>
              )}
           </div>
          
          <div className="p-12 max-w-[1700px] mx-auto min-h-full">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {children}
            </motion.div>
          </div>
        </main>
      </div>
      <XORAAssistant />
    </div>
  );
}
